import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createPasskeyRegistrationLinkContract,
  removePasskeyContract,
  type CreatePasskeyRegistrationLinkResponse,
  type RemovePasskeyResponse
} from '@bliks-io/contracts';
import { toast } from 'sonner';
import { apiClient } from '../client';
import { userKeys } from '../query-keys';
import {
  handleSensitiveMutationError,
  type SensitiveMutationOptions
} from '@/lib/auth/stepUpReauth';
import i18next from '@/lib/i18n';

/** Passkey-add has no resume marker (see StepUpAction), so no action payload here. */
interface PasskeyLinkMutationOptions {
  onReauthRequired: () => void;
}

/**
 * Mint a one-time passkey registration link. The returned URL points at
 * Zitadel's hosted setup page (the credential must be created on the auth
 * origin — WebAuthn binds it to that domain); the caller opens it in a new
 * tab. No success toast — the navigation is the feedback.
 */
export function useCreatePasskeyRegistrationLink({ onReauthRequired }: PasskeyLinkMutationOptions) {
  return useMutation({
    mutationFn: async (): Promise<CreatePasskeyRegistrationLinkResponse> => {
      const { data } = await apiClient.post<CreatePasskeyRegistrationLinkResponse>(
        createPasskeyRegistrationLinkContract.path,
        {}
      );
      return data;
    },
    onError: (error: unknown) =>
      handleSensitiveMutationError(
        error,
        'userSettings.security.passkeyLinkError',
        onReauthRequired
      )
  });
}

/** Remove one registered passkey. */
export function useRemovePasskey({ onReauthRequired }: SensitiveMutationOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (passkeyId: string): Promise<RemovePasskeyResponse> => {
      const { data } = await apiClient.delete<RemovePasskeyResponse>(
        removePasskeyContract.path.replace(':passkeyId', passkeyId)
      );
      return data;
    },
    onSuccess: () => {
      toast.success(i18next.t('userSettings.security.passkeyRemoveSuccess'));
      return queryClient.invalidateQueries({ queryKey: userKeys.security() });
    },
    onError: (error: unknown, passkeyId: string) =>
      handleSensitiveMutationError(error, 'userSettings.security.passkeyRemoveError', () =>
        onReauthRequired({ kind: 'passkey-remove', passkeyId })
      )
  });
}
